import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  useDataProvider
} from 'react-admin';
import { withStyles } from '@material-ui/core/styles';
import { sanitizeConsentTexts, getLanguagesFromConsentTexts, handleAPIErrors, sanitizeTemplate } from '../../../utils';
import { CreateBody } from '../../../components/CreateBody';
import { getAccountId } from '../../../token';
import CreateHeader from '../../../components/CreateHeader';
import { StyledPage } from '../../../components/StyledPage';
import CreationForm from './CreationForm';

const styles = {
  root: {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '&& > div': {
      boxShadow: 'none',
      overflow: 'initial',
      backgroundColor: 'transparent'
    },
  },
  createForm: {
    width: '700px',
  },
};

const SimpleFormWithCustomSubmit = (props) => {
  const [languages, setLanguages] = useState([]);
  const [consentTexts, setConsentTexts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const dataProvider = useDataProvider();

  useEffect(() => {
    getConsentTexts().then(consentTexts => {
      getLanguagesFromConsentTexts(dataProvider, consentTexts)
        .then(setLanguages)
        .catch((error) => console.error(error));
    });
  }, []);

  const getChosenChoiceBoxTemplate = (choiceBoxTemplateId, account, consentSolutionName) => {
    return dataProvider.getOne('choice_box_templates', {
      id: choiceBoxTemplateId
    })
      .then(choiceBoxTemplatesResponse => sanitizeTemplate(choiceBoxTemplatesResponse.data, account, consentSolutionName))
      .catch(error => {
        handleAPIErrors(error, props.history);
        console.error(error);
      });
  };

  const getChosenDeclarationTemplate = (declarationTemplateId, account, consentSolutionName) => {
    return dataProvider.getOne('declaration_templates', {
      id: declarationTemplateId
    })
      .then(declarationTemplateResponse => sanitizeTemplate(declarationTemplateResponse.data, account, consentSolutionName))
      .catch(error => {
        handleAPIErrors(error, props.history);
        console.error(error);
      });
  };

  const getChosenPopUpTemplate = (popUpTemplateId, account, consentSolutionName) => {
    return dataProvider.getOne('pop_up_templates', {
      id: popUpTemplateId
    })
      .then(popUpTemplatesResponse => sanitizeTemplate(popUpTemplatesResponse.data, account, consentSolutionName))
      .catch(error => {
        handleAPIErrors(error, props.history);
        console.error(error);
      });
  };

  const getConsentTexts = () => {
    return dataProvider.getList('cookie_consent_texts', {
      filter: {
        isGlobal: true,
        itemsPerPage: 100
      },
      pagination: {
        page: 1
      },
      sort: {
        field: 'website',
        order: 'ASC'
      }
    })
      .then((consentTextsResponse) => {
        setConsentTexts(sanitizeConsentTexts(consentTextsResponse.data))

        return consentTextsResponse.data;
      })
      .catch(error => {
        handleAPIErrors(error, props.history);
        console.error(error);
      });
  };


  const submitForm = (record) => {
    setIsSaving(true);
    let payload = buildWebsiteCreationPayload(record);

    Promise.all([
      getChosenPopUpTemplate(record.popUpTemplate, record.account, record.name),
      getChosenDeclarationTemplate(record.declarationTemplate, record.account, record.name),
      getChosenChoiceBoxTemplate(record.choiceBoxTemplate, record.account, record.name)
    ])
      .then(results => {
        const [popUpResult, declarationResult, choiceBoxResult] = results;

        payload.popUpTemplate = popUpResult;
        payload.declarationTemplate = declarationResult;
        payload.choiceBoxTemplate = choiceBoxResult;

        props.save(payload, getRedirectUrl);
      })
      .catch(e => console.error(e))
      .finally(()=>{
        setIsSaving(false);
      });
  };

  const getRedirectUrl = (basePath, id) => {
    return `${id}/installation`;
  };

  const buildWebsiteCreationPayload = (record) => {
    let selectedLanguages = (record.languages) ? [record['defaultLang'], ...record.languages] : [record['defaultLang']];

    record.domains = buildDomainsPayload(record.domains, record.defaultLang);
    record.cookieConsentTexts = buildConsentTextsPayload(selectedLanguages);
    record.name = record.domains[0].canonicalDomain;

    delete record['defaultLang'];
    delete record['languages'];
    record.publishNow = true;

    if (!record.account) {
      record.account = `/accounts/${getAccountId()}`;
    }

    return record;
  };

  const buildDomainsPayload = (domains, defaultLang) => {
    if (Array.isArray(domains)) {
      return domains.map((domain) => {
        domain.displayedDomain = domain.canonicalDomain;
        domain.defaultLang = defaultLang;
        if (domain.scanningDepth === null || domain.scanningFrequency === null) {
          domain.scanningDepth = 0;
          domain.scanningFrequency = 0;
        }
        return domain;
      });
    } else {
      return [];
    }
  };

  const buildConsentTextsPayload = (selectedLanguages) => {
    return consentTexts.filter((singleConsentText) => {
      return selectedLanguages.indexOf(singleConsentText.languages) !== -1;
    });
  };

  return (
    <SimpleForm save={submitForm}
      toolbar={<CreationForm {...props} isSaving={isSaving || props.saving} languages={languages} />}>
    </SimpleForm>
  );
};

export const WebsitesCreate = withStyles(styles)(({ classes, ...props }) => (
  <StyledPage className={classes.createForm}>
    <CreateHeader title="Create Consent Solution" />
    <CreateBody {...props} className={classes.root}>
      <SimpleFormWithCustomSubmit />
    </CreateBody>
  </StyledPage>
));
